import React, { useCallback } from 'react';
import type { NodeType } from '../../../types/workflow';

interface ToolbarItem {
  type: NodeType;
  label: string;
  icon: string;
  color: string;
  description: string;
}

const toolbarItems: ToolbarItem[] = [
  {
    type: 'start',
    label: '开始',
    icon: '🚀',
    color: '#22c55e',
    description: '工作流的起始节点',
  },
  {
    type: 'ai',
    label: 'AI',
    icon: '🤖',
    color: '#a855f7',
    description: '调用AI模型处理',
  },
  {
    type: 'condition',
    label: '条件',
    icon: '🔀',
    color: '#eab308',
    description: '条件分支判断',
  },
  {
    type: 'api',
    label: 'API',
    icon: '🔌',
    color: '#3b82f6',
    description: '调用外部API',
  },
  {
    type: 'end',
    label: '结束',
    icon: '🏁',
    color: '#ef4444',
    description: '工作流的结束节点',
  },
];

interface NodeToolbarProps {
  onDragStart: (event: React.DragEvent, nodeType: NodeType) => void;
}

export default function NodeToolbar({ onDragStart }: NodeToolbarProps) {
  const handleDragStart = useCallback(
    (event: React.DragEvent, item: ToolbarItem) => {
      // 设置多种数据格式以确保兼容性
      event.dataTransfer.setData('application/reactflow', item.type);
      event.dataTransfer.setData('text/plain', item.type);
      event.dataTransfer.effectAllowed = 'move';
      
      // 设置拖拽图像（可选，提升用户体验）
      const dragImage = event.currentTarget.cloneNode(true) as HTMLElement;
      dragImage.style.position = 'absolute';
      dragImage.style.top = '-1000px';
      dragImage.style.opacity = '0.8';
      document.body.appendChild(dragImage);
      event.dataTransfer.setDragImage(dragImage, 28, 28);
      setTimeout(() => document.body.removeChild(dragImage), 0);
      
      onDragStart(event, item.type);
    },
    [onDragStart]
  );

  return (
    <div
      style={{
        width: '72px',
        height: '100%',
        background: '#f9fafb',
        borderRight: '1px solid #e5e7eb',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: '12px 0',
        gap: '8px',
      }}
    >
      <div
        style={{
          fontSize: '10px',
          fontWeight: 600,
          color: '#6b7280',
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
          marginBottom: '4px',
        }}
      >
        节点
      </div>

      {toolbarItems.map((item) => (
        <div
          key={item.type}
          draggable
          onDragStart={(e) => handleDragStart(e, item)}
          style={{
            width: '56px',
            height: '56px',
            background: 'white',
            border: `2px solid ${item.color}`,
            borderRadius: '12px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'grab',
            transition: 'all 0.2s ease',
            boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'scale(1.05)';
            e.currentTarget.style.boxShadow = `0 4px 12px ${item.color}40`;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'scale(1)';
            e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.05)';
          }}
          title={item.description}
        >
          <span style={{ fontSize: '20px', marginBottom: '2px' }}>{item.icon}</span>
          <span
            style={{
              fontSize: '10px',
              fontWeight: 600,
              color: item.color,
            }}
          >
            {item.label}
          </span>
        </div>
      ))}

      <div
        style={{
          marginTop: 'auto',
          padding: '8px',
          fontSize: '10px',
          color: '#9ca3af',
          textAlign: 'center',
        }}
      >
        拖拽到画布
      </div>
    </div>
  );
}
